import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { UserCog, Loader2, Save } from 'lucide-react';

export default function MijnAccount() {
  const { user, refreshMe } = useAuth();
  const { toast } = useToast();
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const wantsPasswordChange = newPassword.length > 0 || confirmPassword.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (wantsPasswordChange) {
      if (!currentPassword) { setError('Huidig wachtwoord is verplicht om je wachtwoord te wijzigen'); return; }
      if (newPassword.length < 10) { setError('Nieuw wachtwoord moet minstens 10 tekens zijn'); return; }
      if (newPassword !== confirmPassword) { setError('Wachtwoorden komen niet overeen'); return; }
    }
    setSaving(true);
    try {
      const payload = { name: name.trim() };
      if (wantsPasswordChange) {
        payload.current_password = currentPassword;
        payload.new_password = newPassword;
      }
      await api.put('/users/me', payload);
      await refreshMe();
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      toast({ title: 'Account bijgewerkt' });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Bijwerken mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6" data-testid="page-mijn-account">
      <div className="border-b border-[#e4e9f0] pb-4">
        <h2 className="text-3xl font-bold text-tw1-brand-navy flex items-center gap-2">
          <UserCog size={26} /> Mijn account
        </h2>
        <p className="mt-1 text-sm text-tw1-text-muted">Je naam en wachtwoord bijwerken.</p>
      </div>

      <form onSubmit={submit} className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">E-mail</label>
          <input value={user?.email || ''} disabled className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Naam</label>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            data-testid="mijn-account-name" required
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-tw1-accent-gold focus:outline-none"
          />
        </div>

        <div className="border-t border-slate-100 pt-4">
          <p className="mb-3 text-sm font-medium text-slate-700">Wachtwoord wijzigen (optioneel)</p>
          <div className="space-y-3">
            <input
              type="password" placeholder="Huidig wachtwoord" value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password"
              data-testid="mijn-account-current-password"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-tw1-accent-gold focus:outline-none"
            />
            <input
              type="password" placeholder="Nieuw wachtwoord (min. 10 tekens)" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password"
              data-testid="mijn-account-new-password"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-tw1-accent-gold focus:outline-none"
            />
            <input
              type="password" placeholder="Bevestig nieuw wachtwoord" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password"
              data-testid="mijn-account-confirm-password"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-tw1-accent-gold focus:outline-none"
            />
          </div>
        </div>

        {error && (
          <div role="alert" data-testid="mijn-account-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit" disabled={saving} data-testid="mijn-account-submit"
          className="flex items-center gap-2 rounded-md bg-tw1-brand-navy px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-tw1-brand-navy/90 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Opslaan
        </button>
      </form>
    </div>
  );
}
